// src/components/NameForm.jsx

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { logUxEvent } from './telemetry/uxLogger';
import ProgressBar from './components/ProgressBar';
import FormBackButton from './components/FormBackButton';
import DecoDivider from './components/DecoDivider';
import useViewportInsets from './hooks/useViewportInsets';
import useFormSubmitGuard from './hooks/useFormSubmitGuard';
import { persistIdentityKey } from './utils/identityBackup';
import { ensureSignupSession } from './utils/ensureSignupSession';
import {
  markSignupFieldTyped,
  readSignupFieldTypedHere,
} from './utils/signupFieldPrefill';
import {
  nextSignupRoute,
  prevReachableSignupRoute,
  getSignupVariant,
  isFirstStep,
} from './utils/signupOrderAb';
import {
  isValidSignupFullName,
  signupFullNameErrorReason,
  SIGNUP_NAME_ERROR_COPY,
  normalizeSignupFullName,
} from './utils/patientNameGuard';
import { usesGuidedCopy, signupUxProgress } from './utils/signupUxAb';
import { signupCta, signupStepTitle } from './utils/signupUxCopy';
import { isOrgHost } from './lib/siteHosts';
import {
  readSavedSignupIdentity,
  startSignupQuickResume,
  cancelSignupQuickResume,
  startSignupAsAnotherPerson,
  maskEmailForDisplay,
} from './utils/signupQuickResume';
import FieldValidCheck, { FIELD_VALID_CHECK_CSS } from './components/FieldValidCheck';
import SignupStepHeading from './components/SignupStepHeading';
import {
  assignSignupPriceNoticeArm,
  showsSignupPriceNotice,
  SIGNUP_PRICE_LABEL,
} from './utils/signupPriceNoticeAb';

const BACKEND_URL = process.env.REACT_APP_API_URL || window.location.origin;

const AUTO_PATIENT_ID_RE = /^patient-\d{6,}(?:-[a-z0-9]+)*$/i;

const isLikelyAutoPatientId = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return false;
  return AUTO_PATIENT_ID_RE.test(raw);
};

const NameForm = () => {
  const navigate = useNavigate();
  useViewportInsets();

  // Banner mobile "baixe o app"
  const [showMobileBanner, setShowMobileBanner] = useState(false);

  // Voltar no mesmo funil (mesma aba) relê o nome acabado de confirmar —
  // o AnimatedOutlet remonta o NameForm e, sem isto, o campo some.
  // A marca é o que garante que foi ESTA aba que digitou: o boot restaura
  // `fullName` de cookie/IndexedDB, que são do aparelho, e antes disso o nome
  // de quem usou antes reaparecia aqui numa consulta nova depois de um reload.
  const [fullName, setFullName] = useState(() => {
    const cached = readSignupFieldTypedHere('fullName');
    if (cached && !isLikelyAutoPatientId(cached) && isValidSignupFullName(cached)) return cached;
    return '';
  });
  const [touched, setTouched] = useState(false);
  const inputRef = useRef(null);

  const [priceNoticeArm] = useState(() => assignSignupPriceNoticeArm());
  useEffect(() => {
    if (!priceNoticeArm) return;
    logUxEvent('signup_price_notice_exposure', { arm: priceNoticeArm });
  }, [priceNoticeArm]);

  // Identidade salva no aparelho. Só oferece o atalho quando esta aba ainda
  // não digitou um nome (voltar no funil não reabre o cartão).
  const [savedIdentity, setSavedIdentity] = useState(null);
  const anotherPersonRef = useRef(false);
  useEffect(() => {
    if (readSignupFieldTypedHere('fullName')) return undefined;
    let cancelled = false;
    readSavedSignupIdentity()
      .then((identity) => {
        if (cancelled || !identity) return;
        setSavedIdentity(identity);
        logUxEvent('signup_quick_resume_shown', {
          has_email: !!identity.email,
          has_cpf: !!identity.cpf,
          has_phone: !!identity.phone,
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  /**
   * Toque na moldura do campo ou no corpo do card leva o foco ao input. As
   * métricas de `dead_click` em /consulta mostram a maior parte do atrito da
   * tela aqui: a pessoa mira o campo e acerta a borda (`field-wrapper`) ou a
   * área do formulário, e nada acontece. Só reage quando o alvo é o próprio
   * elemento, para não roubar o clique do botão Continuar nem dos rótulos.
   */
  const focusNameInputOnOwnClick = (e) => {
    if (e.target !== e.currentTarget) return;
    try {
      inputRef.current?.focus();
    } catch {}
  };

  // CRÍTICO: Limpa classes de scroll lock órfãs ao montar
  // Isso resolve scroll travado quando usuário navega de ConsultaChat com paywall aberto
  useEffect(() => {
    const body = document.body;
    
    // Remove classes de scroll lock órfãs (se não há overlay visível)
    if (body.classList.contains('paywall-open') && !document.querySelector('.paywall-overlay')) {
      body.classList.remove('paywall-open');
    }
    if (body.classList.contains('scroll-locked') && !document.querySelector('.paywall-overlay')) {
      body.classList.remove('scroll-locked');
    }
    
    // Limpa estilos inline que podem travar scroll
    body.style.overflow = '';
    body.style.position = '';
    body.style.touchAction = '';
    body.style.width = '';
    body.style.height = '';
    body.style.top = '';
    body.style.left = '';
  }, []);

  useEffect(() => {
    // Remove qualquer resíduo em localStorage para não misturar com dados clínicos
    try {
      const cached = localStorage.getItem('fullName');
      if (cached && (isLikelyAutoPatientId(cached) || !isValidSignupFullName(cached))) {
        localStorage.removeItem('fullName');
      }
    } catch {}
  }, []);

  const isValidName = isValidSignupFullName(fullName);
  const guided = usesGuidedCopy();

  const DEMO_NAMES = ['revisor playstore', 'conta demonstracao', 'conta demonstração'];
  const DEMO_DATA = {
    email: 'revisor@consultamais.app',
    cpf: '52998224725',
    phone: '41999999999',
  };

  // Deep link direto em /consulta sem passar pela Home: garante sessão + braço
  // sorteados uma vez. Se já vieram pela Home (patientId + braço no LS), a
  // função é idempotente e não apaga nada. Idempotente também no re-mount
  // (React StrictMode em dev roda o efeito duas vezes).
  const sessionReadyRef = useRef(false);
  useEffect(() => {
    if (sessionReadyRef.current) return;
    sessionReadyRef.current = true;
    ensureSignupSession().catch(() => {});
  }, []);

  // Sempre que o paciente confirma o nome, o A/B de ordem decide para onde
  // ir. NameForm não é mais o único ponto de criação de patientId — o CTA da
  // Home já criou tudo. Aqui só gravamos o nome (sem apagar campos que outro
  // braço já preencheu) e chamamos /registration/step (upsert no backend).
  // `quickIdentity` vem do "Continuar como": nome salvo no aparelho, sem
  // passar pelo campo.
  const handleConfirmRaw = async (quickIdentity) => {
    const isQuick = !!(quickIdentity && quickIdentity.fullName);
    const nameValue = isQuick ? quickIdentity.fullName : fullName;
    setTouched(true);
    if (!isValidSignupFullName(nameValue)) {
      logUxEvent('name_validation_error', {
        reason: signupFullNameErrorReason(nameValue) || 'invalid_full_name',
        length: String(nameValue || '').trim().length,
      });
      return false;
    }

    const session = anotherPersonRef.current
      ? await startSignupAsAnotherPerson()
      : await ensureSignupSession().catch(() => null);
    anotherPersonRef.current = false;
    const patientId =
      (session && session.patientId) ||
      sessionStorage.getItem('patientId') ||
      localStorage.getItem('patientId') ||
      '';

    const normalizedName = normalizeSignupFullName(nameValue);

    try {
      sessionStorage.setItem('fullName', normalizedName);
      localStorage.setItem('fullName', normalizedName);
      sessionStorage.setItem('patientName', normalizedName);
      localStorage.setItem('patientName', normalizedName);
    } catch {}
    try { persistIdentityKey('fullName', normalizedName); } catch {}
    try { persistIdentityKey('patientName', normalizedName); } catch {}
    markSignupFieldTyped('fullName');
    logUxEvent('name_confirmed', {
      length: normalizedName.length,
      hasHyphen: normalizedName.includes('-'),
      signupStep: 'nome',
      ...(isQuick ? { quick_resume: true } : {}),
    });

    const isDemo = DEMO_NAMES.includes(nameValue.trim().toLowerCase());
    if (isDemo) {
      sessionStorage.setItem('patientEmail', DEMO_DATA.email);
      localStorage.setItem('patientEmail', DEMO_DATA.email);
      sessionStorage.setItem('patientCpf', DEMO_DATA.cpf);
      localStorage.setItem('patientCpf', DEMO_DATA.cpf);
      sessionStorage.setItem('patientPhone', DEMO_DATA.phone);
      localStorage.setItem('patientPhone', DEMO_DATA.phone);
      try { persistIdentityKey('patientEmail', DEMO_DATA.email); } catch {}
      try { persistIdentityKey('patientCpf', DEMO_DATA.cpf); } catch {}
      try { persistIdentityKey('patientPhone', DEMO_DATA.phone); } catch {}
      logUxEvent('demo_mode_activated');
      navigate('/termos');
      return true;
    }

    if (patientId) {
      try {
        await fetch(`${BACKEND_URL}/registration/step`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            patientId,
            step: 'nome',
            fullName: normalizedName,
          }),
        });
      } catch {}
    }

    if (isQuick) {
      const steps = startSignupQuickResume(quickIdentity);
      logUxEvent('signup_quick_resume_used', { steps: steps.join(',') });
    }

    const variant = getSignupVariant();
    const dest = nextSignupRoute('nome', variant) || '/email';
    navigate(dest);
    return true;
  };

  const [isSubmitting, handleConfirm] = useFormSubmitGuard(handleConfirmRaw);

  const continueAsSaved = () => {
    if (!savedIdentity) return;
    handleConfirm(savedIdentity);
  };

  const dismissSavedIdentity = () => {
    anotherPersonRef.current = true;
    cancelSignupQuickResume();
    setSavedIdentity(null);
    setFullName('');
    setTouched(false);
    logUxEvent('signup_quick_resume_other_person', {});
    setTimeout(() => {
      try { inputRef.current?.focus(); } catch {}
    }, 60);
  };

  const savedFieldsCopy = (() => {
    if (!savedIdentity) return '';
    const parts = [];
    if (savedIdentity.email) parts.push(`o e-mail ${maskEmailForDisplay(savedIdentity.email)}`);
    if (savedIdentity.cpf) parts.push('o CPF');
    if (savedIdentity.phone) parts.push('o telefone');
    if (!parts.length) return '';
    const list = parts.length === 1
      ? parts[0]
      : `${parts.slice(0, -1).join(', ')} e ${parts[parts.length - 1]}`;
    return `Usaremos ${list} da sua última consulta neste aparelho.`;
  })();

  // Banner mobile "baixe o app"
  useEffect(() => {
    const dismissed = localStorage.getItem('hideMobileAppBanner') === '1';
    
    const isInNativeApp = () => {
      if (typeof window.ReactNativeWebView !== 'undefined') return true;
      if (typeof window.webkit !== 'undefined' && window.webkit?.messageHandlers) return true;
      if (/ConsultaMaisApp/i.test(navigator.userAgent)) return true;
      if (localStorage.getItem('isNativeApp') === '1') return true;
      return false;
    };
    
    const isStandalonePWA = window.matchMedia?.('(display-mode: standalone)').matches || 
                           window.navigator.standalone === true;
    
    const isMobile = window.innerWidth <= 768 && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    const show = isMobile && !dismissed && !isStandalonePWA && !isInNativeApp();
    setShowMobileBanner(show);
  }, []);

  const closeMobileBanner = () => {
    localStorage.setItem('hideMobileAppBanner', '1');
    setShowMobileBanner(false);
    logUxEvent('nameform_banner_dismissed', { variant: 'mobile_app' });
  };

  return (
    <>
      <style>{`
        ${FIELD_VALID_CHECK_CSS}
        /* Poppins loaded globally in index.html */

        /* ===== LINK DISCRETO: BAIXAR APP (mobile web) ===== */
        .app-hint {
          display: inline-flex;
          align-items: center;
          gap: .35rem;
          margin-top: .35rem;
          font-size: .75rem;
          color: var(--text-dim);
        }
        .app-hint a {
          color: var(--primary);
          font-weight: 600;
          text-decoration: none;
        }
        .app-hint__close {
          background: transparent;
          border: none;
          cursor: pointer;
          color: var(--text-dim);
          font-size: 1rem;
          line-height: 1;
          padding: 0 .15rem;
          opacity: .7;
        }
        .app-hint__close:hover { opacity: 1; }

        :root {
          --primary: #D32F2F;
          --bg-page: #F9FAFB;
          --surface: #FFFFFF;
          --text-main: #1E1E1E;
          --text-dim: #6B6B6B;
          --border-subtle: #E5E7EB;
          --error: #D32F2F;
          --radius-lg: 1rem;
          --radius-xl: 1.5rem;
          --radius-full: 999px;
          --shadow-xs: 0 1px 2px rgba(0,0,0,0.04);
          --shadow-sm: 0 6px 16px rgba(0,0,0,0.06);
          --shadow-lg: 0 12px 28px rgba(0,0,0,0.08);
        }

        body {
          margin: 0;
          background: var(--bg-page);
          font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Inter', 'Roboto', sans-serif;
          color: var(--text-main);
          -webkit-font-smoothing: antialiased;
        }

        /* ===== LAYOUT CONTAINER ===== */
        .page-shell {
          min-height: 100vh;
          min-height: 100dvh;
          display: flex;
          flex-direction: column;
          align-items: stretch;
          background: var(--bg-page);
        }

        /* padding-bottom usa --vvb (altura do teclado virtual quando aberto)
           e env(safe-area-inset-bottom) para garantir que o botão Continuar
           nunca fique escondido atrás do teclado em iOS/Android. */
        .inner-wrap {
          flex: 1;
          max-width: 480px;
          width: 100%;
          margin: 0 auto;
          padding: 4.5rem 1rem max(2rem, calc(1rem + var(--vvb, 0px) + env(safe-area-inset-bottom, 0px)));
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 1.25rem;
          box-sizing: border-box;
          transition: padding-bottom 0.18s ease;
        }

        /* Garante que o input rolar para a viewport quando focado, deixando
           o botão visível imediatamente abaixo dele. */
        .text-field {
          scroll-margin-bottom: calc(120px + env(safe-area-inset-bottom, 0px));
          scroll-margin-top: 1rem;
        }

        /* ===== HEADER TEXTO SUPERIOR ===== */
        .header-block {
          text-align: center;
          max-width: 22rem;
          margin-top: 1rem;
          margin-bottom: 2rem;
        }

        .header-eyebrow {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: .5rem;
          background: #FFF1F1;
          color: var(--primary);
          font-size: .7rem;
          font-weight: 600;
          line-height: 1.2;
          border-radius: var(--radius-full);
          padding: .5rem .75rem;
          box-shadow: var(--shadow-xs);
          margin-bottom: 1rem;
        }

        .header-eyebrow-bullet {
          width: .5rem;
          height: .5rem;
          background: var(--primary);
          border-radius: 50%;
          box-shadow: 0 0 6px rgba(211,47,47,0.6);
        }

        .main-headline {
          font-size: 1.25rem;
          line-height: 1.35;
          font-weight: 600;
          color: var(--text-main);
          margin: 0 0 .75rem;
        }

        .sub-headline {
          font-size: .9rem;
          line-height: 1.4;
          font-weight: 400;
          color: var(--text-dim);
          margin: 0 auto;
        }

        /* ===== FORM CARD ===== */
        .form-card {
          width: 100%;
          max-width: 400px;
          background: var(--surface);
          border-radius: var(--radius-xl);
          box-shadow: var(--shadow-sm);
          padding: 1.25rem 1.25rem 1rem;
          border: 1px solid rgba(0,0,0,0.06);
          display: flex;
          flex-direction: column;
          align-items: stretch;
          box-sizing: border-box;
        }

        .label-row {
          display: flex;
          flex-direction: column;
          margin-bottom: .75rem;
        }

        .label-title {
          font-size: .8rem;
          font-weight: 500;
          line-height: 1.3;
          color: var(--text-main);
          display: flex;
          justify-content: space-between;
          align-items: baseline;
          width: 100%;
          box-sizing: border-box;
        }

        .label-hint {
          font-size: .7rem;
          font-weight: 400;
          color: var(--text-dim);
          line-height: 1.3;
        }

        /* WRAPPER DO INPUT (corrige overflow) */
        .field-wrapper {
          width: 100%;
          background: var(--surface);
          border-radius: var(--radius-lg);
          border: 1px solid var(--border-subtle);
          box-shadow: none;
          box-sizing: border-box;
          display: flex;
          align-items: center;
          padding: 0;
          overflow: hidden; /* impede o "estouro" visual */
          transition: border-color .18s ease, box-shadow .18s ease, background .18s ease;
        }

        .text-field {
          flex: 1 1 auto;
          width: auto;
          min-width: 0;
          border: none;
          outline: none;
          font-size: 1.05rem;
          line-height: 1.4;
          color: var(--text-main);
          background: transparent;
          padding: 1rem 1.1rem;
          box-sizing: border-box;
          -webkit-appearance: none;
        }

        .text-field:focus {
          outline: none;
        }

        /* halo de foco agora fica no wrapper */
        .field-wrapper:focus-within {
          border: 2px solid var(--primary);
          box-shadow: 0 0 0 3px rgba(211,47,47,0.10);
          background: #FFF;
        }

        .error-text {
          min-height: 1.25rem;
          font-size: .8rem;
          line-height: 1.4;
          font-weight: 500;
          color: var(--error);
          margin-top: .6rem;
          margin-bottom: 1rem;
        }

        /* ===== BOTÃO CONFIRMAR ===== */
        .confirm-wrap {
          display: flex;
          justify-content: center;
          margin-top: .5rem;
          margin-bottom: .5rem;
        }

        .confirm-button {
          background: var(--primary);
          color: #FFF;
          border: none;
          border-radius: var(--radius-full);
          box-shadow: none;
          font-size: 1.05rem;
          line-height: 1.2;
          font-weight: 600;
          padding: 1rem 1.5rem;
          min-width: 200px;
          min-height: 48px;
          cursor: pointer;
          transition: background .18s ease, opacity .18s ease;
          -webkit-tap-highlight-color: transparent;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: .5rem;
        }
        .confirm-button__spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255,255,255,0.4);
          border-top-color: #fff;
          border-radius: 50%;
          animation: nf-spin 0.8s linear infinite;
        }
        @keyframes nf-spin { to { transform: rotate(360deg); } }
        .confirm-button:hover:not(:disabled) {
          background: #B71C1C;
        }
        .confirm-button:focus-visible {
          box-shadow: 0 0 0 3px rgba(211,47,47,0.12);
        }

        .confirm-button:disabled {
          opacity: .6;
          cursor: not-allowed;
          box-shadow: var(--shadow-xs);
        }

        .confirm-button:active:not(:disabled) {
          transform: scale(.98);
          box-shadow: var(--shadow-sm);
        }

        .name-doc-preview {
          margin: 0 0 1rem;
          padding: .7rem .85rem;
          border-radius: var(--radius-lg);
          background: #FFF8F8;
          border: 1px solid #F3D6D6;
          text-align: left;
        }
        .name-doc-preview__label {
          margin: 0 0 .25rem;
          font-size: .7rem;
          font-weight: 500;
          line-height: 1.35;
          color: var(--text-dim);
        }
        .name-doc-preview__value {
          margin: 0;
          font-size: 1.02rem;
          font-weight: 600;
          line-height: 1.35;
          color: var(--text-main);
          word-break: break-word;
        }

        .quick-resume {
          align-items: center;
          text-align: center;
          gap: .75rem;
          padding: 1.25rem;
        }
        .quick-resume__label {
          margin: 0;
          font-size: .8rem;
          font-weight: 500;
          color: var(--text-dim);
        }
        .quick-resume__continue {
          width: 100%;
          min-width: 0;
          white-space: normal;
          word-break: break-word;
        }
        .quick-resume__hint {
          margin: 0;
          font-size: .75rem;
          line-height: 1.4;
          color: var(--text-dim);
        }
        .quick-resume__other {
          background: transparent;
          border: none;
          color: var(--primary);
          font-size: .9rem;
          font-weight: 600;
          padding: .6rem 1rem;
          min-height: 44px;
          cursor: pointer;
          -webkit-tap-highlight-color: transparent;
        }

        .legal-hint {
          text-align: center;
          font-size: .7rem;
          line-height: 1.4;
          color: var(--text-dim);
          font-weight: 400;
          margin-top: .75rem;
          user-select: none;
        }

        /* ===== CONTATO FIXO NO RODAPÉ ===== */
        .contact-link {
          margin-top: auto;
          text-align: center;
          font-size: .8rem;
          line-height: 1.4;
          color: var(--text-main);
          font-weight: 400;
          padding: 0 1rem calc(1.5rem + env(safe-area-inset-bottom));
          box-sizing: border-box;
        }

        .contact-link a {
          color: var(--primary);
          font-weight: 600;
          text-decoration: none;
        }

        /* ===== RESPONSIVO ===== */
        @media (min-width: 480px) {
          .inner-wrap {
            padding: 5.5rem 2rem 4rem;
          }

          .main-headline {
            font-size: 1.4rem;
          }

          .sub-headline {
            font-size: .9rem;
          }

          .confirm-button {
            min-width: 220px;
            font-size: 1rem;
          }
        }

        @media (min-width: 768px) {
          .main-headline {
            font-size: 1.5rem;
          }
          .sub-headline {
            font-size: .9rem;
          }
        }
      `}</style>

      <div className="page-shell">
        <div className="inner-wrap">
          <FormBackButton
            onClick={() => {
              const variant = getSignupVariant();
              if (isFirstStep('/consulta', variant)) {
                navigate('/');
                return;
              }
              const prev = prevReachableSignupRoute('nome', variant);
              navigate(prev || '/');
            }}
            ariaLabel="Voltar"
          />

          <ProgressBar {...signupUxProgress('/consulta', getSignupVariant())} />

          <div className="header-block">
            <h1 className="main-headline deco-headline">Qual é o nome completo do paciente?</h1>
            <DecoDivider variant="chevron" />
            {showsSignupPriceNotice(priceNoticeArm) ? (
              <p className="sub-headline" data-testid="signup-price-notice">
                Consulta por chat com médico: <strong>{SIGNUP_PRICE_LABEL}</strong> no PIX ou em até 12x no
                cartão. O pagamento vem depois da triagem.
              </p>
            ) : null}
          </div>

          {savedIdentity ? (
            <div className="form-card quick-resume" data-testid="signup-quick-resume">
              <p className="quick-resume__label">Encontramos seus dados neste aparelho</p>
              <button
                type="button"
                className="confirm-button quick-resume__continue"
                onClick={continueAsSaved}
                disabled={isSubmitting}
              >
                {isSubmitting ? <span className="confirm-button__spinner" aria-hidden="true" /> : null}
                <span>Continuar como {savedIdentity.fullName}</span>
              </button>
              {savedFieldsCopy ? <p className="quick-resume__hint">{savedFieldsCopy}</p> : null}
              <button
                type="button"
                className="quick-resume__other"
                onClick={dismissSavedIdentity}
                disabled={isSubmitting}
              >
                É outra pessoa
              </button>
            </div>
          ) : (
          /* CARD DO FORM */
          <form
            className="form-card"
            onSubmit={(e) => {
              e.preventDefault();
              handleConfirm();
            }}
            onClick={focusNameInputOnOwnClick}
            noValidate
          >
            {guided ? <SignupStepHeading title={signupStepTitle('/consulta')} /> : null}
            <div className="label-row">
              <div className="label-title">
                <label htmlFor="patient-name">Nome completo do paciente</label>
              </div>
              <div
                className="label-hint"
                id="patient-name-hint"
                onClick={() => { try { document.getElementById('patient-name')?.focus(); } catch {} }}
              >Ex.: “Maria da Silva e Souza”</div>
            </div>

            {/* wrapper impede o input de sair do card */}
            <div
              className="field-wrapper deco-field-focus"
              onClick={focusNameInputOnOwnClick}
            >
              <input
                id="patient-name"
                ref={inputRef}
                type="text"
                className="text-field"
                placeholder="Digite o nome completo do paciente"
                value={fullName}
                onChange={(e) => {
                  setFullName(e.target.value);
                  if (!touched) setTouched(true);
                }}
                onKeyDown={(e) => {
                  // Garante validacao mesmo quando o submit esta `disabled` —
                  // browsers nao disparam o submit nesse caso, mas queremos
                  // que Enter exiba a mensagem de erro e setTouched.
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleConfirm();
                  }
                }}
                onFocus={(e) => {
                  const node = e.currentTarget;
                  setTimeout(() => {
                    try { node.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch {}
                  }, 220);
                }}
                autoComplete="name"
                autoCapitalize="words"
                spellCheck={false}
                enterKeyHint="next"
                aria-invalid={touched && !isValidName}
                aria-describedby={isValidName ? 'patient-name-hint patient-name-error patient-name-preview' : 'patient-name-hint patient-name-error'}
                aria-required="true"
              />
              {guided && <FieldValidCheck show={isValidName} label="Nome válido" />}
            </div>

            <div className="error-text" id="patient-name-error" role={touched && !isValidName ? 'alert' : undefined}>
              {touched && !isValidName
                ? (SIGNUP_NAME_ERROR_COPY[signupFullNameErrorReason(fullName)] || SIGNUP_NAME_ERROR_COPY.shape)
                : ' '}
            </div>

            {isValidName ? (
              <div
                className="name-doc-preview"
                id="patient-name-preview"
                role="status"
                aria-live="polite"
              >
                <p className="name-doc-preview__label">
                  {isOrgHost()
                    ? 'Este nome sai no atestado e nos demais documentos:'
                    : 'Este nome sai no atestado e na receita:'}
                </p>
                <p className="name-doc-preview__value">{normalizeSignupFullName(fullName)}</p>
              </div>
            ) : null}

            <div className="confirm-wrap">
              <button
                type="submit"
                className="confirm-button"
                disabled={!isValidName || isSubmitting}
              >
                {signupCta('nome', { guided, submitting: isSubmitting })}
              </button>
            </div>
            <p className="legal-hint">
              {isValidName
                ? 'Não escreva o que você está sentindo aqui.'
                : isOrgHost()
                  ? 'Este nome sai no atestado e nos demais documentos, igual ao documento de identidade. Não escreva o que você está sentindo aqui.'
                  : 'Este nome sai no atestado e na receita, igual ao documento. Não escreva o que você está sentindo aqui.'}
            </p>
          </form>
          )}

          {showMobileBanner && (
            <div className="contact-link">
              <div className="app-hint" role="note" aria-label="Baixar aplicativo">
                <span>Prefere atendimento pelo aplicativo?</span>
                <a
                  href="https://consultamais.app/baixar"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Baixar
                </a>
                <button
                  type="button"
                  aria-label="Fechar sugestão de app"
                  className="app-hint__close"
                  onClick={closeMobileBanner}
                >
                  ×
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default NameForm;
